import React, { useEffect, useState } from "react";
import { api, UGX } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState } from "../components/Common";
import { Building2, Users, Banknote, ShieldAlert, Bell, PauseCircle, PlayCircle, Timer, Plus, Trash2, Key, HardDrive, Database, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdmin() {
  const [tenants, setTenants] = useState([]);
  const [storage, setStorage] = useState(null);
  const [health, setHealth] = useState(null);
  const [backup, setBackup] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [editFor, setEditFor] = useState(null);

  const load = async () => {
    try {
      const [{data:t},{data:s},{data:h},{data:b}] = await Promise.all([
        api.get("/admin/tenants"),
        api.get("/admin/storage"),
        api.get("/admin/billing-health"),
        api.get("/admin/backup-status"),
      ]);
      setTenants(t); setStorage(s); setHealth(h); setBackup(b);
    } catch { toast.error("Requires super_admin role"); }
  };
  useEffect(() => { load(); }, []);

  const action = async (org_id, act, label) => {
    try { await api.post("/admin/subscription-action", { organization_id: org_id, action: act }); toast.success(label); load(); }
    catch { toast.error("Action failed"); }
  };
  const del = async (org_id) => {
    if (!window.confirm("Soft-delete this organization? Patient records are retained.")) return;
    try { await api.delete(`/admin/organizations/${org_id}`); toast.success("Organization deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader title="Super Admin · Platform" subtitle="Tenants, storage, billing health & backups." actions={
        <button data-testid="create-org-btn" onClick={()=>setShowCreate(true)} className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> Create organization</button>
      } />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Active tenants" value={health?.active ?? "—"} icon={Building2} tone="green" />
        <KpiCard label="Overdue" value={health?.overdue ?? "—"} icon={ShieldAlert} tone="amber" />
        <KpiCard label="Trial ending soon" value={health?.trial_ending_soon ?? "—"} icon={Timer} tone="blue" />
        <KpiCard label="Churn rate" value={`${health?.churn_rate_pct ?? 0}%`} icon={PauseCircle} tone="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Storage">
          {!storage ? <EmptyState title="Loading…" /> : (
            <div className="p-4 space-y-2 text-sm">
              <Row label="Database" value={`${storage.estimated_db_mb} MB`} icon={Database} />
              <Row label="Total documents" value={storage.total_documents.toLocaleString()} />
              <Row label="File storage" value={`${storage.estimated_files_mb} MB`} />
              <Row label="Images" value={`${storage.estimated_images_mb} MB`} />
              <Row label="Lab attachments" value={`${storage.estimated_lab_attachments_mb} MB`} />
              <Row label="Backup size" value={`${storage.backup_size_mb.toFixed(1)} MB`} />
              <Row label="Remaining" value={`${storage.remaining_gb} GB / ${storage.quota_gb} GB`} />
            </div>
          )}
        </SectionCard>
        <SectionCard title="Billing health">
          {!health ? <EmptyState title="Loading…" /> : (
            <div className="p-4 space-y-2 text-sm">
              <Row label="Grace period" value={health.grace} />
              <Row label="Suspended" value={health.suspended} />
              <Row label="Failed payments" value={health.failed_payments} />
              <Row label="Total tenants" value={health.total} />
              <Row label="Churn rate" value={`${health.churn_rate_pct}%`} />
            </div>
          )}
        </SectionCard>
        <SectionCard title="Backup & DR">
          {!backup ? <EmptyState title="Loading…" /> : (
            <div className="p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2"><span className="chip chip-green">{backup.status}</span> <span className="text-slate-500">{backup.frequency}</span></div>
              <Row label="Last backup" value={new Date(backup.last_backup_at).toLocaleString()} icon={HardDrive} />
              <Row label="Next backup" value={new Date(backup.next_backup_at).toLocaleString()} />
              <Row label="Retention" value={`${backup.retention_days} days`} />
              <Row label="Storage" value={backup.storage_location} />
              <Row label="DR" value={backup.dr_status} />
              <div className="flex gap-2 pt-2">
                <button data-testid="restore-backup" onClick={()=>toast.info("Restore initiated (demo). Confirmation email sent to admin.")} className="flex-1 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium">Restore</button>
                <button data-testid="download-backup" onClick={()=>toast.info("Backup download link expires in 10 min (demo).")} className="flex-1 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium">Download</button>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Tenants">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Organization</th><th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Plan</th><th className="text-right px-4 py-2.5">Monthly</th>
                <th className="text-right px-4 py-2.5">Users</th><th className="text-right px-4 py-2.5">Patients</th>
                <th className="text-left px-4 py-2.5">Status</th><th className="text-right px-4 py-2.5">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tenants.map(t => (
                <tr key={t.organization_id} data-testid={`tenant-${t.organization_id}`}>
                  <td className="px-4 py-3"><div className="font-medium">{t.name}</div><div className="text-xs text-slate-500">{t.district} · {t.branch}</div></td>
                  <td className="px-4 py-3">{t.type}</td>
                  <td className="px-4 py-3">{t.plan}</td>
                  <td className="px-4 py-3 text-right tabular">{UGX(t.monthly_fee)}</td>
                  <td className="px-4 py-3 text-right tabular">{t.user_count||0}</td>
                  <td className="px-4 py-3 text-right tabular">{t.patient_count||0}</td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <button data-testid={`reset-pw-${t.organization_id}`} onClick={()=>setResetFor(t)} className="px-2 h-7 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] font-medium flex items-center gap-1"><Key className="w-3 h-3" /> Reset PW</button>
                      {t.status!=="deleted" && t.status!=="suspended" && (
                        <button data-testid={`edit-${t.organization_id}`} onClick={()=>setEditFor(t)} className="px-2 h-7 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] font-medium flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                      )}
                      {t.status==="grace" && (
                        <button data-testid={`remind-${t.organization_id}`} onClick={()=>action(t.organization_id,"send_reminder","Reminder sent")} className="px-2 h-7 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] font-medium flex items-center gap-1"><Bell className="w-3 h-3" /> Remind</button>
                      )}
                      {["active","trial","grace"].includes(t.status) && (
                        <button data-testid={`suspend-${t.organization_id}`} onClick={()=>{ if(window.confirm(`Suspend ${t.name}?\n\nAll active sessions for this hospital will be terminated immediately and users will be blocked from logging in until you reactivate.`)) action(t.organization_id,"suspend","Suspended — sessions terminated"); }} className="px-2 h-7 rounded-md bg-[#fdecec] text-[#B71C1C] hover:bg-[#f9d5d5] text-[11px] font-medium flex items-center gap-1"><PauseCircle className="w-3 h-3" /> Suspend</button>
                      )}
                      {t.status==="suspended" && (
                        <button data-testid={`reactivate-${t.organization_id}`} onClick={()=>action(t.organization_id,"reactivate","Reactivated — normal access restored")} className="px-2 h-7 rounded-md bg-[#e6f4ea] text-[#1B5E20] hover:bg-[#c9e7d1] text-[11px] font-medium flex items-center gap-1"><PlayCircle className="w-3 h-3" /> Reactivate</button>
                      )}
                      {t.status!=="deleted" && (
                        <button data-testid={`delete-${t.organization_id}`} onClick={()=>del(t.organization_id)} className="px-2 h-7 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] text-[#B71C1C] font-medium flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {showCreate && <CreateOrgModal onClose={()=>setShowCreate(false)} onDone={()=>{setShowCreate(false);load();}} />}
      {resetFor && <ResetPasswordModal org={resetFor} onClose={()=>setResetFor(null)} />}
      {editFor && <EditOrgModal org={editFor} onClose={()=>setEditFor(null)} onDone={()=>{setEditFor(null);load();}} />}
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between"><span className="text-slate-500 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</span><span className="font-medium tabular">{value}</span></div>
  );
}

function CreateOrgModal({ onClose, onDone }) {
  const [f, setF] = useState({ name:"", type:"Clinic", district:"Kampala", branch:"Main", plan:"Clinic Plan", monthly_fee:1000000, admin_email:"", admin_name:"", admin_password:"", status:"trial", trial_days:30, features:{dentist:false,optical:false,orthotics:false}, speciality_only:false, dhis2_org_unit:"" });
  const toggleFeat = (k) => setF({ ...f, features: { ...f.features, [k]: !f.features[k] } });
  // Auto-preset when the type is a speciality clinic
  const onTypeChange = (v) => {
    let next = { ...f, type: v };
    if (v === "Dental Clinic") { next.features = { ...f.features, dentist: true }; next.speciality_only = true; }
    else if (v === "Optical Clinic") { next.features = { ...f.features, optical: true }; next.speciality_only = true; }
    else if (v === "Orthotics Clinic") { next.features = { ...f.features, orthotics: true }; next.speciality_only = true; }
    setF(next);
  };
  const submit = async () => {
    try {
      const payload = { ...f };
      if (payload.status !== "trial") delete payload.trial_days;
      const {data} = await api.post("/admin/organizations", payload);
      toast.success(`Created ${data.organization.name} (${payload.status})`);
      onDone();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="create-org-modal">
        <div className="font-semibold mb-1">Create organization</div>
        <div className="text-xs text-slate-500 mb-3">Trial tenants do not count toward Active Tenants or MRR until you convert them.</div>
        <div className="grid grid-cols-2 gap-3">
          <TextField testId="co-name" label="Name" value={f.name} onChange={v=>setF({...f,name:v})} />
          <SelectField testId="co-type" label="Type / speciality" value={f.type} onChange={onTypeChange} options={["Hospital","Clinic","Medical Centre","Laboratory","Pharmacy","Imaging Centre","Dental Clinic","Optical Clinic","Orthotics Clinic"]} />
          <TextField testId="co-district" label="District" value={f.district} onChange={v=>setF({...f,district:v})} />
          <TextField testId="co-branch" label="Branch" value={f.branch} onChange={v=>setF({...f,branch:v})} />
          <SelectField testId="co-plan" label="Plan" value={f.plan} onChange={v=>setF({...f,plan:v})} options={["Clinic Plan","Hospital Plan","Laboratory Plan"]} />
          <TextField testId="co-fee" label="Monthly fee (UGX)" value={f.monthly_fee} onChange={v=>setF({...f,monthly_fee:Number(v)})} />
          <SelectField testId="co-status" label="Status" value={f.status} onChange={v=>setF({...f,status:v})} options={[["trial","Demo / Trial"],["active","Active (billable)"]]} />
          {f.status === "trial" ? (
            <TextField testId="co-trial-days" label="Trial length (days)" value={f.trial_days} onChange={v=>setF({...f,trial_days:Math.max(1, Number(v)||1)})} />
          ) : (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-800 self-end">Billable from day one · counts toward Active Tenants</div>
          )}
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 p-3 bg-slate-50">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Speciality clinic modules</div>
          <div className="grid grid-cols-3 gap-2">
            <FeatChk testId="co-feat-dentist"   label="Dental"    on={f.features.dentist}   onChange={()=>toggleFeat("dentist")} />
            <FeatChk testId="co-feat-optical"   label="Optical"   on={f.features.optical}   onChange={()=>toggleFeat("optical")} />
            <FeatChk testId="co-feat-orthotics" label="Orthotics" on={f.features.orthotics} onChange={()=>toggleFeat("orthotics")} />
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs">
            <input data-testid="co-speciality-only" type="checkbox" checked={f.speciality_only} onChange={e=>setF({...f,speciality_only:e.target.checked})} className="w-4 h-4" />
            <span className="text-slate-700"><b>Speciality-only clinic</b> — hide inpatient, ward-round and admitted-patient modules from the sidebar (the hospital only runs the selected speciality workflow).</span>
          </label>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 p-3 bg-slate-50">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">HMIS · Ministry of Health reporting</div>
          <TextField
            testId="co-dhis2-org-unit"
            label="DHIS2 org unit code (optional)"
            value={f.dhis2_org_unit}
            onChange={v=>setF({...f,dhis2_org_unit:v})}
            placeholder="e.g. UG-Kla-STMR-001"
          />
          <div className="text-[11px] text-slate-500 mt-1">Prints on every HMIS 105/108 CSV export so the Records Officer's file matches the MoH facility register. Leave blank if you don't have a code yet.</div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <TextField testId="co-admin-name" label="Admin name" value={f.admin_name} onChange={v=>setF({...f,admin_name:v})} />
          <TextField testId="co-admin-email" label="Admin email" value={f.admin_email} onChange={v=>setF({...f,admin_email:v})} />
          <TextField testId="co-admin-pw" label="Admin password (min 8)" value={f.admin_password} onChange={v=>setF({...f,admin_password:v})} type="password" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="co-submit" onClick={submit} className="px-5 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Create</button>
        </div>
      </div>
    </div>
  );
}

function FeatChk({ testId, label, on, onChange }) {
  return (
    <label className={`flex items-center gap-2 px-3 h-10 rounded-lg border cursor-pointer text-sm ${on ? "bg-[#1976D2] text-white border-[#1565C0] font-semibold" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
      <input data-testid={testId} type="checkbox" checked={on} onChange={onChange} className="w-4 h-4" />
      {label}
    </label>
  );
}

function EditOrgModal({ org, onClose, onDone }) {
  const [f, setF] = useState({
    name: org.name || "",
    type: org.type || "Clinic",
    district: org.district || "",
    branch: org.branch || "Main",
    plan: org.plan || "Clinic Plan",
    monthly_fee: org.monthly_fee || 0,
    status: org.status === "active" ? "active" : "trial",
    trial_days: org.trial_days || 30,
    features: { dentist: !!org.features?.dentist, optical: !!org.features?.optical, orthotics: !!org.features?.orthotics },
    speciality_only: !!org.speciality_only,
    dhis2_org_unit: org.dhis2_org_unit || "",
  });
  const toggleFeat = (k) => setF({ ...f, features: { ...f.features, [k]: !f.features[k] } });
  const wasTrial = org.status === "trial";
  const submit = async () => {
    try {
      const payload = { ...f };
      if (payload.status !== "trial") delete payload.trial_days;
      const { data } = await api.patch(`/admin/organizations/${org.organization_id}`, payload);
      const msg = wasTrial && f.status === "active"
        ? `${data.organization.name} converted to Active — now billable`
        : `${data.organization.name} updated`;
      toast.success(msg);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const trialEnd = org.trial_end ? new Date(org.trial_end).toLocaleDateString() : null;
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="edit-org-modal">
        <div className="font-semibold mb-1">Edit organization</div>
        <div className="text-xs text-slate-500 mb-3">
          {org.name}
          {wasTrial && trialEnd && <> · <span className="text-amber-700 font-medium">Trial ends {trialEnd}</span></>}
        </div>
        {wasTrial && f.status === "active" && (
          <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
            You're converting this tenant from <b>Trial</b> to <b>Active</b>. From save-time it will count toward Active Tenants and MRR; the trial-end date will be cleared.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <TextField testId="eo-name" label="Name" value={f.name} onChange={v=>setF({...f,name:v})} />
          <SelectField testId="eo-type" label="Type / speciality" value={f.type} onChange={v=>setF({...f,type:v})} options={["Hospital","Clinic","Medical Centre","Laboratory","Pharmacy","Imaging Centre","Dental Clinic","Optical Clinic","Orthotics Clinic"]} />
          <TextField testId="eo-district" label="District" value={f.district} onChange={v=>setF({...f,district:v})} />
          <TextField testId="eo-branch" label="Branch" value={f.branch} onChange={v=>setF({...f,branch:v})} />
          <SelectField testId="eo-plan" label="Plan" value={f.plan} onChange={v=>setF({...f,plan:v})} options={["Clinic Plan","Hospital Plan","Laboratory Plan"]} />
          <TextField testId="eo-fee" label="Monthly fee (UGX)" value={f.monthly_fee} onChange={v=>setF({...f,monthly_fee:Number(v)})} />
          <SelectField testId="eo-status" label="Status" value={f.status} onChange={v=>setF({...f,status:v})} options={[["trial","Demo / Trial"],["active","Active (billable)"]]} />
          {f.status === "trial" ? (
            <TextField testId="eo-trial-days" label={wasTrial ? "Reset trial length (days)" : "Trial length (days)"} value={f.trial_days} onChange={v=>setF({...f,trial_days:Math.max(1, Number(v)||1)})} />
          ) : (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-800 self-end">Billable · counts toward Active Tenants</div>
          )}
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 p-3 bg-slate-50">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Speciality clinic modules</div>
          <div className="grid grid-cols-3 gap-2">
            <FeatChk testId="eo-feat-dentist"   label="Dental"    on={f.features.dentist}   onChange={()=>toggleFeat("dentist")} />
            <FeatChk testId="eo-feat-optical"   label="Optical"   on={f.features.optical}   onChange={()=>toggleFeat("optical")} />
            <FeatChk testId="eo-feat-orthotics" label="Orthotics" on={f.features.orthotics} onChange={()=>toggleFeat("orthotics")} />
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs">
            <input data-testid="eo-speciality-only" type="checkbox" checked={f.speciality_only} onChange={e=>setF({...f,speciality_only:e.target.checked})} className="w-4 h-4" />
            <span className="text-slate-700"><b>Speciality-only clinic</b> — hide inpatient, ward-round and admitted-patient modules from the sidebar.</span>
          </label>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 p-3 bg-slate-50">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">HMIS · Ministry of Health reporting</div>
          <TextField
            testId="eo-dhis2-org-unit"
            label="DHIS2 org unit code"
            value={f.dhis2_org_unit}
            onChange={v=>setF({...f,dhis2_org_unit:v})}
            placeholder="e.g. UG-Kla-STMR-001"
          />
          <div className="text-[11px] text-slate-500 mt-1">Prints on every HMIS 105/108 CSV export so the Records Officer's file matches the MoH facility register. Leave blank to clear.</div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="eo-submit" onClick={submit} className="px-5 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Save changes</button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ org, onClose }) {
  const [pw, setPw] = useState("");
  const submit = async () => {
    try { const {data} = await api.post("/admin/reset-admin-password", { organization_id: org.organization_id, new_password: pw }); toast.success(`Reset for ${data.admin_email}`); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={e=>e.stopPropagation()} data-testid="reset-pw-modal">
        <div className="font-semibold">Reset admin password</div>
        <div className="text-sm text-slate-500 mb-3">{org.name}</div>
        <TextField testId="reset-pw-new" label="New password (min 8)" value={pw} onChange={setPw} type="password" />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="reset-pw-submit" onClick={submit} className="px-5 h-10 rounded-lg bg-[#1976D2] text-white text-sm font-semibold">Reset</button>
        </div>
      </div>
    </div>
  );
}

function TextField({ testId, label, value, onChange, type="text", placeholder="" }) {
  return <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">{label}</span>
    <input data-testid={testId} type={type} value={value} placeholder={placeholder} onChange={e=>onChange(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
  </label>;
}
function SelectField({ testId, label, value, onChange, options }) {
  const norm = options.map(o => Array.isArray(o) ? o : [o, o]);
  return <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">{label}</span>
    <select data-testid={testId} value={value} onChange={e=>onChange(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
      {norm.map(([v,l])=><option key={v} value={v}>{l}</option>)}
    </select>
  </label>;
}
